import type { CSSProperties } from 'react';

import { formatCurrency } from '../lib/format';
import { PLATFORM_LABEL } from '../lib/platforms';
import type { OverlayTheme, SenderPlatform } from '../services/api';

/**
 * Renderização do ALERTA do overlay — compartilhada entre a página
 * /overlay/:token (dentro do OBS) e a pré-visualização da dashboard.
 *
 * Os 8 templates prontos são variações de CSS sobre a mesma estrutura; o
 * modo 'custom' renderiza o HTML do criador com os placeholders
 * substituídos por valores ESCAPADOS (o espectador nunca injeta HTML).
 */

export interface AlertData {
  senderName: string;
  message: string;
  amountCents: number;
  senderPlatform?: SenderPlatform | null;
}

/** Escapa texto vindo do espectador antes de entrar no HTML custom. */
function escapeHtml(value: string): string {
  return value
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;')
    .replaceAll("'", '&#39;');
}

/** Substitui os placeholders do template custom pelos dados do alerta. */
function renderCustomHtml(theme: OverlayTheme, alert: AlertData): string {
  return theme.customHtml
    .replaceAll('{{nome}}', escapeHtml(alert.senderName))
    .replaceAll('{{mensagem}}', escapeHtml(alert.message))
    .replaceAll('{{valor}}', escapeHtml(formatCurrency(alert.amountCents)))
    .replaceAll(
      '{{plataforma}}',
      alert.senderPlatform ? escapeHtml(PLATFORM_LABEL[alert.senderPlatform]) : '',
    )
    .replaceAll('{{logo_url}}', escapeHtml(theme.logoUrl));
}

export function OverlayAlertView({ theme, alert }: { theme: OverlayTheme; alert: AlertData }) {
  // As cores escolhidas viram variáveis CSS que os templates consomem
  const style = {
    '--alert-accent': theme.accentColor,
    '--alert-bg': theme.backgroundColor,
    '--alert-text': theme.textColor,
  } as CSSProperties;

  if (theme.template === 'custom') {
    // HTML do PRÓPRIO CRIADOR (a URL do overlay é secreta e só ele configura
    // o template) — os dados do espectador entram sempre escapados
    return (
      <div
        className="alert-view alert-view--custom"
        style={style}
        dangerouslySetInnerHTML={{ __html: renderCustomHtml(theme, alert) }}
      />
    );
  }

  return (
    <div className={`alert-view alert-view--${theme.template}`} style={style}>
      {theme.logoUrl && <img className="alert-view__logo" src={theme.logoUrl} alt="" />}
      <div className="alert-view__body">
        <div className="alert-view__header">
          <span className="alert-view__sender">
            {alert.senderName}
            {alert.senderPlatform && (
              <span className="alert-view__platform">
                via {PLATFORM_LABEL[alert.senderPlatform]}
              </span>
            )}
          </span>
          <span className="alert-view__amount">{formatCurrency(alert.amountCents)}</span>
        </div>
        <p className="alert-view__message">{alert.message}</p>
      </div>
    </div>
  );
}
